import { Injectable, inject } from '@angular/core';
import {
  CoreRouterActionNavigateForward,
  CoreRouterActions,
  CoreUtilsService
} from '@bepatient/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, from, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { MyEdiaryApiService } from './my-ediary-api.service';
import {
  MyEdiaryActions,
  MyEdiaryActionTypes,
  MyEdiaryActionLoadEdiarySuccess,
  MyEdiaryActionLoadEdiaryFailed,
  MyEdiaryActionLoadEdiaryPending,
  MyEdiaryActionAddToCalendar,
  MyEdiaryActionAddToCalendarSuccess,
  MyEdiaryActionAddToCalendarFailed,
  MyEdiaryActionFindAndAddToCalendar,
  MyEdiaryActionFindEventExists
} from './my-ediary-store.actions';
import { Calendar } from '@awesome-cordova-plugins/calendar/ngx';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class MyEdiaryStoreEffects {
  private actions$ = inject(Actions);
  private store$ = inject<Store<any>>(Store);
  private ediaryService = inject(MyEdiaryApiService);
  private calendar = inject(Calendar);
  private coreUtils = inject(CoreUtilsService);
  private translate = inject(TranslateService);

  loadEdiary$: Observable<MyEdiaryActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(MyEdiaryActionTypes.LOAD_EDIARY),
      switchMap(action => {
        this.store$.dispatch(new MyEdiaryActionLoadEdiaryPending(true));
        return this.ediaryService.loadEDiary().pipe(
          map(res => {
            return new MyEdiaryActionLoadEdiarySuccess(res);
          }),
          catchError(e => {
            return of(new MyEdiaryActionLoadEdiaryFailed());
          }),
          finalize(() =>
            this.store$.dispatch(new MyEdiaryActionLoadEdiaryPending(false))
          )
        );
      })
    )
  );

  loadEdiaryDetail$: Observable<CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(MyEdiaryActionTypes.LOAD_EDIARY_DETAIL),
      map(() => {
        return new CoreRouterActionNavigateForward({
          url: 'ediary/detail'
        });
      })
    )
  );

  findEventAndAddToCalendar$: Observable<MyEdiaryActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(MyEdiaryActionTypes.FIND_AND_ADD_TO_CALENDAR),
      map((action: MyEdiaryActionFindAndAddToCalendar) => action.payload),
      switchMap(calendarEvent => {
        return from(
          this.calendar.findEventWithOptions(
            calendarEvent.title,
            calendarEvent.location,
            calendarEvent.notes,
            calendarEvent.startDate,
            calendarEvent.endDate,
            calendarEvent.options
          )
        ).pipe(
          map(res =>
            res && res.length > 0
              ? new MyEdiaryActionFindEventExists()
              : new MyEdiaryActionAddToCalendar(calendarEvent)
          ),
          catchError(err => of(new MyEdiaryActionAddToCalendarFailed()))
        );
      })
    )
  );

  addToCalendar$: Observable<MyEdiaryActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(MyEdiaryActionTypes.ADD_TO_CALENDAR),
      map((action: MyEdiaryActionAddToCalendar) => action.payload),
      switchMap(calendarEvent => {
        return from(
          this.calendar.createEventInteractivelyWithOptions(
            calendarEvent.title,
            calendarEvent.location,
            calendarEvent.notes,
            calendarEvent.startDate,
            calendarEvent.endDate,
            calendarEvent.options
          )
        ).pipe(
          map(res => new MyEdiaryActionAddToCalendarSuccess(res)),
          catchError(err => of(new MyEdiaryActionAddToCalendarFailed()))
        );
      })
    )
  );

  addToCalendarSuccess$: Observable<MyEdiaryActions> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MyEdiaryActionTypes.ADD_TO_CALENDAR_SUCCESS),
        map((action: MyEdiaryActionAddToCalendarSuccess) => action.payload),
        tap(payload => {
          if (payload) {
            const msg = this.translate.instant('ADD_TO_CALENDAR.SUCCESS');
            this.coreUtils.presentToaster(msg);
          }
        })
      ),
    { dispatch: false }
  );

  eventAlreadyExist$: Observable<MyEdiaryActions> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MyEdiaryActionTypes.FIND_EVENT_EXISTS),
        tap(() => {
          const msg = this.translate.instant('ADD_TO_CALENDAR.EXISTS');
          this.coreUtils.presentToaster(msg);
        })
      ),
    { dispatch: false }
  );

  addToCalendarFailed$: Observable<MyEdiaryActions> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MyEdiaryActionTypes.ADD_TO_CALENDAR_FAILED),
        tap(() => {
          const msg = this.translate.instant('ADD_TO_CALENDAR.ERROR');
          this.coreUtils.presentToaster(msg);
        })
      ),
    { dispatch: false }
  );
}
